import { CommonModule } from "@angular/common";
import { AfterViewInit, Component, ElementRef, EventEmitter, HostBinding, inject, Input, OnInit, Output } from "@angular/core";
import { FormControl, FormsModule } from "@angular/forms";
import { MatButtonToggleModule } from "@angular/material/button-toggle";
import { MatCheckboxModule } from "@angular/material/checkbox";
import { MatChipsModule } from "@angular/material/chips";
import { MatRippleModule } from "@angular/material/core";
import { MatExpansionModule } from "@angular/material/expansion";
import { MatIconModule } from "@angular/material/icon";
import { MatSidenavModule } from "@angular/material/sidenav";
import { MatSlideToggleModule } from "@angular/material/slide-toggle";
import { TranslateModule } from "@ngx-translate/core";
import { DefaultComponent } from "src/app/default.component";
import { ROUTES_CONFIG } from "src/config/routes.config";
import { TooltipDirective } from "src/directives/tooltip.directive";
import { Key } from "src/enums/key";
import { ActionResponse, ParsedActionResponse } from "src/interfaces/post-request/post-request";
import { ExtractHtmlPipe } from "src/pipes/extract-html.pipe";
import { ApplicationService } from "src/services/application.service";
import { HttpService } from "src/services/http.service";
import { TableGroupAmountPipe } from "../../../../pipes/table-group-amount.pipe";
import { DialogService, DialogTemplate } from "../../global/dialog/dialog.service";
import {
  TableDeleteWarningComponent,
  TableDeleteWarningDialogData,
} from "../../global/dialog/impl/table-delete-warning/table-delete-warning.component";
import { TableFilterDialogComponent, TableFilterDialogData } from "../../global/dialog/impl/table-filter-dialog/table-filter-dialog.component";
import { PrefixOmsComponent } from "../../global/prefixes/impl/prefix-oms/prefix-oms.component";
import { PrefixEvent, PrefixEventType } from "../../global/prefixes/prefix.component";
import { LinkData } from "../../global/prefixes/templates/template-link/template-link.component";
import { ButtonAction, ButtonEvent, ButtonsComponent } from "../buttons/buttons.component";
import { TableGroup } from "./table-group";
import { TableHeader } from "./table-header";
import { TableRow } from "./table-row";
import { TableListViewComponent } from "./table-view/table-list-view/table-list-view.component";
import { TableMapViewComponent } from "./table-view/table-map-view/table-map-view.component";
import { TableTileViewComponent } from "./table-view/table-tile-view/table-tile-view.component";
import { TableContentPart, TableContentPartFilter, TableOptions, TableResponseButton } from "./table.interface";

export interface TableAction {
  type: ButtonAction;
  params: unknown[];
  table: TableComponent;
}

type TableView = "list" | "tile" | "map";

@Component({
  selector: "app-table",
  imports: [
    MatChipsModule,
    MatIconModule,
    TranslateModule,
    MatExpansionModule,
    MatCheckboxModule,
    MatSlideToggleModule,
    CommonModule,
    FormsModule,
    MatButtonToggleModule,
    MatRippleModule,
    TableListViewComponent,
    TableGroupAmountPipe,
    PrefixOmsComponent,
    TooltipDirective,
    MatSidenavModule,
    ButtonsComponent,
    TableTileViewComponent,
    TableMapViewComponent,
  ],
  templateUrl: "./table.component.html",
  styleUrls: ["./table.component.less", "./table.print.less"],
})
export class TableComponent extends DefaultComponent implements OnInit, AfterViewInit {
  private dialog = inject(DialogService);
  private http = inject(HttpService);
  private application = inject(ApplicationService);

  @HostBinding("style.minHeight")
  public minHeight: string;

  @Input()
  public id: string;

  @Input()
  public headers: TableHeader[];

  @Input()
  public root: TableGroup | null;

  @Input()
  public options: TableOptions;

  @Input()
  public step: number;

  @Input()
  public setstatusaction: string;

  @Input()
  public searchactionid: string;

  @Input()
  public sortactionid: string;

  @Input()
  public filters: TableContentPart["groupfilters"];

  @Input()
  public buttons: TableResponseButton[];

  @Input()
  public searchkeys: string[];

  public tableContainer: ElementRef | null;

  @Output()
  public event: EventEmitter<TableAction>;

  public view: TableView;

  public searchControl: FormControl<string | null> | null;

  public drawerAnimating: boolean;

  public filtersActive: boolean;

  public selectedRows: TableRow[];

  public page: number;

  private searchHighlight: Highlight | undefined;

  private filterStates: Map<string, boolean>;
  private extractHtmlPipe: ExtractHtmlPipe;

  public constructor() {
    super();
    this.id = "";
    this.extractHtmlPipe = inject(ExtractHtmlPipe);

    this.headers = [];
    this.root = null;
    this.options = {
      head: true,
      foot: true,
      delete: false,
      archive: false,
      add: false,
      download: false,
      search: true,
      filter: true,
      resultslimited: false,
      resultslimitedmaxreached: false,
      selectable: true,
      map: false,
      tile: false,
      pageSize: 25,
      layout: null,
    };
    this.step = 20;
    this.setstatusaction = "";
    this.searchactionid = "";
    this.sortactionid = "";
    this.filters = [];
    this.buttons = [];
    this.searchkeys = [];
    this.view = "list";

    this.tableContainer = inject(ElementRef);
    this.searchControl = null;

    this.searchHighlight = undefined;

    this.filterStates = new Map();
    this.filtersActive = false;

    this.drawerAnimating = false;
    this.event = new EventEmitter();
    this.selectedRows = [];
    this.page = 1;
    this.minHeight = "unset";
  }

  public ngOnInit(): void {
    if (!navigator.userAgent.toLowerCase().includes("firefox")) {
      this.searchHighlight = new Highlight();
      CSS.highlights.set("search", this.searchHighlight);
    }

    this.options.filter = this.getAllGroups().length > 1;
    this.options.selectable = this.options.download || this.options.delete || this.buttons.some((button) => button.id === "17");

    if (this.filters) {
      this.filters.forEach((value) => {
        value.filters.forEach((subvalue) => {
          this.filterStates.set(`${subvalue.level}|${subvalue.value}`, subvalue.active === "true");
        });
      });
    }

    this.hideIDColumn();
    this.applySearch();
    this.applyFilters();

    const firstSortHeader = this.headers.find((header) => header.sort && header.sort.direction !== "INITIAL");
    if (firstSortHeader) this.sortRows(firstSortHeader);
  }

  public searchShortcuts(keycode: string): void {
    if (this.searchControl) {
      switch (keycode) {
        case Key.ENTER:
          this.setSearchkeys();
          break;

        case Key.ESCAPE:
          this.clearSearchkeys();
          break;
      }
    } else {
      throw new Error("Undefined search control");
    }
  }

  public ngAfterViewInit(): void {
    this.applySearchHighlights();
  }

  public setSearchkeys(): void {
    if (this.searchControl) {
      const value = this.searchControl.value || "";
      if (value) {
        this.searchkeys = value.split(" ").filter((i) => i);
        this.onSearchKeyChange();
        this.applySearch();
      } else {
        throw new Error("Invalid search keys.");
      }
    } else {
      throw new Error("Undefined search control.");
    }
  }

  public addSearchkeys(): void {
    if (this.searchControl) {
      const value = this.searchControl.value || "";
      if (value) {
        this.searchkeys = [...new Set([...this.searchkeys, ...value.split(" ")])].filter((i) => i);
        this.applySearch();
        this.onSearchKeyChange();
      } else {
        throw new Error("Invalid search keys.");
      }
    } else {
      throw new Error("Undefined search control.");
    }
  }

  public clearSearchkeys(): void {
    this.searchkeys = [];
    this.applySearch();
    this.applyFilters();
    this.onSearchKeyChange();
  }

  public removeSearchkey(key: string): void {
    this.searchkeys = this.searchkeys.filter((searchkey) => searchkey != key);
    this.applySearch();
    this.applyFilters();
    this.onSearchKeyChange();
  }

  public applySearch(): void {
    const removeTags = (str: string): string => {
      return str.replace(/(<([^>]+)>)/gi, "").replace(/&nbsp;/g, " ");
    };
    const isMatch = (value: string, key: string): boolean => {
      return value.toLowerCase().includes(key.toLowerCase());
    };
    const checkgroup = (group: TableGroup): number => {
      let count = 0;
      count += Array.from(group.rows.values()).filter((row) => !row.hidden && !row.deleted).length;
      for (const subgroups of group.groups.values()) {
        count += checkgroup(subgroups);
      }
      return count;
    };

    if (this.searchControl) this.searchControl.patchValue(null);

    for (const row of this.getAllRows()) {
      const matched: string[] = [];
      for (const key of this.searchkeys) {
        for (const field of row.fields.values()) {
          const value: string = removeTags(String(field.value.value) || "");
          let link = "";
          if (field.extras && "links" in field.extras && field.extras["links"]) {
            link = (<LinkData[]>field.extras?.["links"]).slice().shift()?.value || "";
          }
          if (isMatch(removeTags(String(link)), key) || isMatch(value, key)) {
            matched.push(key);
            break;
          }
        }
      }
      row.hide(matched.length !== this.searchkeys.length);
    }

    for (const group of this.getAllGroups()) {
      group.hidden = checkgroup(group) === 0;
    }

    this.paginate();
  }

  public applyFilters(): void {
    this.updateFiltersActive();
    // if group has child that is visible - this group should stay visible
    const checkGroups = (groups: TableGroup[]): boolean => {
      let hidden = !!groups.length; // set to true if has child groups, dont hide if no childgroups
      for (const group of groups) {
        const result = checkGroup(group);
        if (result === false) {
          hidden = false;
        }
      }
      return hidden;
    };

    // return wether group should stay hidden (true = hidden)
    const checkGroup = (group: TableGroup): boolean => {
      if (this.filterStates.has(`${group.level}|${group.label}`)) {
        if (this.filterStates.get(`${group.level}|${group.label}`) === false) {
          // should be hidden because filter is set to false
          return true;
        } else if (group.groups.length === 0) {
          // filter exists & is set to true, so group should be shown
          return false;
        }
      }
      // Let the child groups decide wether this group should be shown
      return checkGroups(group.groups);
    };

    for (const group of this.getAllGroups()) {
      group.hidden = checkGroup(group);
    }

    this.paginate();
  }

  public onClear(): void {
    if (this.filtersActive) this.toggleAllGroups(false);
    this.clearSearchkeys();
  }

  public toggleAllGroups(toggle = !this.filtersActive): void {
    for (const group of this.getAllGroups()) {
      const filters = this.filters.find((value) => value.level === group.level);
      if (!filters) continue;
      this.toggleFilterGroup(group.level, !toggle, filters.filters);
    }
    // this.switchactive = !toggle;
  }

  public toggleFilterGroup(level: string, checked: boolean, filters: TableContentPartFilter[]): void {
    const index = this.filters.findIndex((val) => val.level === level && val.filters === val.filters);
    for (let i = 0; i < this.filters[index].filters.length; i++) {
      this.filters[index].filters[i].active = checked ? "true" : "false";

      this.toggleFilterIndividual(level, filters[i], checked, false);
    }
    this.applyFilters();
  }

  public toggleFilterIndividual(level: string, filter: TableContentPartFilter, checked: boolean, apply: boolean = true): void {
    this.http
      .send<ActionResponse>(ROUTES_CONFIG.actionurl, {
        FFWDActionID: this.setstatusaction,
        actionData: `${level}|${filter.value}=${checked ? "false" : "true"}`,
      })
      .then((data) => {
        const response = <ParsedActionResponse>JSON.parse(data.postActionAsJSON);
        this.application.onMessage(response.messages ?? []);
      });
    this.filterStates.set(`${level}|${filter.value}`, checked);
    if (apply) {
      this.applyFilters();
    }
  }

  public filterGroupActive(filters: TableContentPartFilter[]): boolean {
    return filters.every((filter) => this.filterStates.get(`${filter.level}|${filter.value}`));
  }

  public filterGroupIndeterminate(filters: TableContentPartFilter[]): boolean {
    return (
      filters.some((filter) => this.filterStates.get(`${filter.level}|${filter.value}`)) &&
      !filters.every((filter) => this.filterStates.get(`${filter.level}|${filter.value}`))
    );
  }

  public filterActive(filter: TableContentPartFilter): boolean {
    return !!this.filterStates.get(`${filter.level}|${filter.value}`);
  }

  public async onEvent(event: ButtonEvent): Promise<void> {
    const type = event.action;
    const params = event.params;

    this.event.emit({
      type: type,
      params: params,
      table: this,
    });
  }

  public deleteRows(): void {
    // const ref =
    this.dialog.open<TableDeleteWarningDialogData>(
      TableDeleteWarningComponent,
      {
        title: {
          label: "DIALOG.TABLE_DELETE_WARNING.TITLE",
        },
        rows: this.selectedRows,
      },
      DialogTemplate.MODAL,
    );
    // if (ref) {
    //   this.addSubscription(
    //     ref.afterClosed().subscribe(() => {
    //       // this.filterShown();
    //       // this.setTotals();
    //     }),
    //   );
    // }
  }

  public onRowSelection(): void {
    this.selectedRows = this.getAllGroups()
      .map((group) => group.getSelected())
      .flat();
  }

  public onPrefixEvent([event, row]: [PrefixEvent, TableRow]): void {
    const type = event.type;
    const params = event.params;

    switch (type) {
      case PrefixEventType.HIGHLIGHT:
        row.highlight(<boolean>params.shift());
        break;
      case PrefixEventType.SNAPSHOT:
        this.event.emit({ type: ButtonAction.SNAPSHOT, params: [params.shift()], table: this });
    }
  }

  public onSort(header: TableHeader): void {
    if (header.sort) {
      const direction = header.sort.direction;

      if (direction == "INITIAL") {
        header.sort.direction = "DESC";
      } else if (direction == "DESC") {
        header.sort.direction = "ASC";
      } else if (direction == "ASC") {
        header.sort.direction = "INITIAL";
      }

      this.sortRows(header);

      this.http.send(ROUTES_CONFIG.actionurl, {
        FFWDActionID: this.sortactionid,
        actionData: `${header.template}${header.label}=${header.sort.direction.toLowerCase()}`,
      });
    }
  }

  public paginate(page = 1): void {
    let rowIndex = 0;
    const start = (page - 1) * this.options.pageSize;
    const end = start + this.options.pageSize;

    const paginateGroupCheck = (group: TableGroup, parentHidden = false): void => {
      if (parentHidden || group.hidden) {
        for (const row of group.rows) {
          row.loaded = false;
          row.selected = false;
        }
        for (const subgroup of group.groups) {
          paginateGroupCheck(subgroup, parentHidden || group.hidden);
        }
      } else {
        for (const row of group.rows) {
          if (!row.hidden) {
            row.loaded = rowIndex >= start && rowIndex < end;
            rowIndex++;
          } else {
            row.loaded = false;
            row.selected = false;
          }
        }
        for (const subgroup of group.groups) {
          paginateGroupCheck(subgroup, parentHidden || group.hidden);
        }
      }
      group.loaded = group.rows.filter((x) => x.loaded);
    };

    if (this.root) paginateGroupCheck(this.root);
    this.page = page;
    this.onRowSelection();
    setTimeout(() => {
      this.applySearchHighlights();
    }, 0);
  }

  public openFilterDialog(): void {
    this.dialog.open<TableFilterDialogData>(TableFilterDialogComponent, { tableInstance: this });
  }

  public getAllGroups(root: TableGroup | null = this.root): TableGroup[] {
    if (!root) return [];
    const groups: TableGroup[] = [];
    groups.push(root);

    for (const group of root.groups) {
      groups.push(...this.getAllGroups(group));
    }

    return groups;
  }

  public setView(newView: typeof this.view): void {
    this.view = newView;
    switch (newView) {
      case "list":
        this.minHeight = "unset";
        break;
      case "tile":
        this.minHeight = "unset";
        break;
      case "map":
        this.minHeight = "100%";
        break;
    }
  }

  private sortRows(header: TableHeader): void {
    if (header.sort) {
      const direction = header.sort.direction;

      for (const _header of this.headers.values()) {
        if (_header === header) continue;
        const sort = _header.sort;
        if (sort && sort.direction) {
          sort.direction = "INITIAL";
        }
      }

      const sortColumn = header.sort.column
        ? Array.from(this.headers.values()).find((h) => `${h.template}${h.label}` === header.sort?.column) || header
        : header;

      /** Checks and sorts group rows and is used resursively */
      const groupCheck = (group: TableGroup, header: TableHeader): void => {
        if (group.rows) {
          let sortedRows: TableRow[] = [];
          /** Sort DESC and ASC */
          if (direction && ["DESC", "ASC"].includes(direction)) {
            const sortedArray = [...group.rows].sort((a, b) => {
              const headerIndex = this.headers.indexOf(header);
              const aHeaderValue = a.fields.at(headerIndex);
              const bHeaderValue = b.fields.at(headerIndex);

              if (aHeaderValue && bHeaderValue) {
                let aValue = <string>aHeaderValue.value.value;
                aValue = this.extractHtmlPipe.transform(aValue).trim().toLowerCase();
                let bValue = <string>bHeaderValue.value.value;
                bValue = this.extractHtmlPipe.transform(bValue).trim().toLowerCase();

                if (direction === "DESC") {
                  if (aValue > bValue) {
                    return -1;
                  }
                  if (aValue < bValue) {
                    return 1;
                  }
                }

                if (direction === "ASC") {
                  if (<string>aValue > <string>bValue) {
                    return 1;
                  }
                  if (<string>aValue < <string>bValue) {
                    return -1;
                  }
                }
              }

              return 0;
            });
            sortedRows = sortedArray;
          }

          /** Sort INITIAL */
          if (direction && direction === "INITIAL") {
            sortedRows = [...group.rows].sort((a, b) => a[0] - b[0]);
          }

          /** Repopulate records */
          group.rows = sortedRows;
          this.paginate();
        }

        if (group.groups.values()) {
          for (const nextGroup of group.groups.values()) {
            groupCheck(nextGroup, sortColumn);
          }
        }
      };

      if (this.root) groupCheck(this.root, sortColumn);
    }
  }

  private hideIDColumn(): void {
    let headerIndex = 0;
    for (const header of this.headers) {
      if (header.label === "ID") {
        let visible = false;
        for (const group of this.getAllGroups()) {
          for (const row of group.rows) {
            const field = row.fields.at(headerIndex);
            if (!field) continue;

            if (field.extras && field.extras["links"]) {
              visible = !!(<unknown[]>field.extras["links"]).length;
              break;
            }
          }
          if (visible) break;
        }
        header.label = "";
        header.visible = visible;
      }
      headerIndex++;
    }
  }

  private async onSearchKeyChange(searchkeys: string[] = this.searchkeys): Promise<void> {
    const actionData = searchkeys.join("|") || "";
    console.log(this.options.resultslimited, this.options.resultslimitedmaxreached);
    if (this.options.resultslimited) {
      this.application.loading.next(true);
      await this.http.send(ROUTES_CONFIG.actionurl, {
        FFWDActionID: this.searchactionid,
        actionData,
      });
      this.application.refresh(this.application.router.url);
    } else {
      this.applySearchHighlights();
      return this.http.send(ROUTES_CONFIG.actionurl, {
        FFWDActionID: this.searchactionid,
        actionData,
      });
    }
  }

  private getAllRows(root: TableGroup | null = this.root): TableRow[] {
    if (!root) return [];
    const rows: TableRow[] = [];
    rows.push(...root.rows);

    for (const group of root.groups) {
      rows.push(...this.getAllRows(group));
    }

    return rows;
  }

  private applySearchHighlights(): void {
    if (!this.searchHighlight) {
      return;
    }

    this.searchHighlight.clear();
    if (!this.searchkeys.length) return;

    const search = (haystack: string, needle: string): { start: number; end: number }[] => {
      const pattern = new RegExp(needle, "gi");
      const occurrences: { start: number; end: number }[] = [];
      let match: RegExpExecArray | null;

      while ((match = pattern.exec(haystack)) !== null) {
        occurrences.push({ start: match.index, end: match.index + match[0].length });
      }

      return occurrences;
    };

    const fields = this.tableContainer?.nativeElement.querySelectorAll("td > .field .search-highlight") ?? [];
    for (const fieldNode of fields) {
      const fieldContent = fieldNode.firstChild;
      if (!fieldContent || !fieldContent.textContent) continue;

      for (const key of this.searchkeys) {
        const matches = search(fieldContent.textContent, key);
        for (const match of matches) {
          const range = new Range();
          range.setStart(fieldNode.firstChild, match.start);
          range.setEnd(fieldNode.firstChild, match.end);

          this.searchHighlight.add(range);
        }
      }
    }
  }

  private updateFiltersActive(): void {
    this.filtersActive = Array.from(this.filterStates.values()).some((filter) => !filter);
  }
}
